import {Async, Empty} from 'Core/models/Async';
import {DateTime} from 'luxon';
import {createAction} from 'Shared/utilities/actionCreator';
import {EarnedActivityReport, OrdinaryActivityReport, Producer} from '../components/A360Commissions/types';

export enum A360CommissionReportingActionType {
  IS_PRODUCER_FILTER_LOADING = 'a360Commission/IS_PRODUCER_FILTER_LOADING',
  IS_REPORT_DATA_LOADING = 'a360Commission/IS_REPORT_DATA_LOADING',
  GET_COMMISSIONS_REPORT_DATA = 'a360Commission/GET_COMMISSIONS_REPORT_DATA',
  CHANGE_COMMISSIONS_REPORT_DATA = 'a360Commission/CHANGE_COMMISSIONS_REPORT_DATA',
  GET_AVAILABLE_PRODUCER_CODES_FOR_REPORTING = 'a360Commission/GET_AVAILABLE_PRODUCER_CODES_FOR_REPORTING',
  CHANGE_AVAILABLE_PRODUCER_CODES_FOR_REPORTING = 'a360Commission/CHANGE_AVAILABLE_PRODUCER_CODES_FOR_REPORTING',
  CHANGE_SELECTED_PRODUCER_CODES_FOR_REPORTING = 'a360Commission/CHANGE_SELECTED_PRODUCER_CODES_FOR_REPORTING',
  CLEAR_A360_COMMISSIONS_REPORTING_STATE = 'a360Commission/CLEAR_A360_COMMISSIONS_REPORTING_STATE',
  CHANGE_SELECTED_DATE_FOR_REPORTING = 'a360Commission/CHANGE_SELECTED_DATE_FOR_REPORTING',
  CHANGE_SELECTED_COMMISSIONS_REPORT = 'a360Commission/CHANGE_SELECTED_COMMISSIONS_REPORT',
  CLEAR_PRODUCER_FILTER = 'a360Commission/CLEAR_PRODUCER_FILTER',
}

export enum CommissionsReport {
  DEFERRED = 'defered',
  EARNED_ACTIVITY = 'earnedActivity',
  ORDINARY = 'ordinary',
}

export type CommissionsReportType = EarnedActivityReport | OrdinaryActivityReport;

export type CommissionsReportData = Array<EarnedActivityReport | OrdinaryActivityReport>;

export type A360CommissionReportingState = {
  availableProducerCodesForReporting: Async<Producer[]>;

  filter: {
    selectedProducerCodes: string[];
    selectedDate: DateTime;
    selectedReport: CommissionsReport;
  };

  isProducerFilterLoading: boolean;
  isReportDataLoading: boolean;
  commissionsReportData: Async<CommissionsReportData>;
};

export const DEFAULT_STATE: A360CommissionReportingState = {
  availableProducerCodesForReporting: new Empty(),

  filter: {
    selectedProducerCodes: [],
    selectedDate: DateTime.local(),
    selectedReport: CommissionsReport.EARNED_ACTIVITY,
  },

  isProducerFilterLoading: false,
  isReportDataLoading: false,
  commissionsReportData: new Empty(),
};

export const changeIsProducerFilterLoading = (isProducerFilterLoading: boolean) =>
  createAction(A360CommissionReportingActionType.IS_PRODUCER_FILTER_LOADING, {isProducerFilterLoading});

export const changeIsReportDataLoading = (isReportDataLoading: boolean) =>
  createAction(A360CommissionReportingActionType.IS_REPORT_DATA_LOADING, {isReportDataLoading});

export const getAvailableProducerCodesForReport = (isNewYork: boolean) =>
  createAction(A360CommissionReportingActionType.GET_AVAILABLE_PRODUCER_CODES_FOR_REPORTING, {isNewYork});

export const changeSelectedProducerCodesForReporting = (producerCodes: string[]) =>
  createAction(A360CommissionReportingActionType.CHANGE_SELECTED_PRODUCER_CODES_FOR_REPORTING, {producerCodes});

export const changeSelectedDateForReporting = (selectedDate: DateTime) =>
  createAction(A360CommissionReportingActionType.CHANGE_SELECTED_DATE_FOR_REPORTING, {selectedDate});

export const changeAvailableProducerCodesForReporting = (producerCodes: Async<Producer[]>) =>
  createAction(A360CommissionReportingActionType.CHANGE_AVAILABLE_PRODUCER_CODES_FOR_REPORTING, {producerCodes});

export const clearA360CommissionsReportingState = () =>
  createAction(A360CommissionReportingActionType.CLEAR_A360_COMMISSIONS_REPORTING_STATE);

export const changeSelectedCommissionsReport = (selectedReport: CommissionsReport) =>
  createAction(A360CommissionReportingActionType.CHANGE_SELECTED_COMMISSIONS_REPORT, {selectedReport});

export const getCommissionsReportData = () =>
  createAction(A360CommissionReportingActionType.GET_COMMISSIONS_REPORT_DATA);

export const clearProducerFilter = () => createAction(A360CommissionReportingActionType.CLEAR_PRODUCER_FILTER);

export const changeCommissionsReportData = (commissionsReportData: Async<CommissionsReportData>) =>
  createAction(A360CommissionReportingActionType.CHANGE_COMMISSIONS_REPORT_DATA, {commissionsReportData});

type A360CommissionReportingAction =
  | ReturnType<typeof changeIsProducerFilterLoading>
  | ReturnType<typeof changeIsReportDataLoading>
  | ReturnType<typeof changeSelectedProducerCodesForReporting>
  | ReturnType<typeof changeAvailableProducerCodesForReporting>
  | ReturnType<typeof changeSelectedDateForReporting>
  | ReturnType<typeof changeSelectedCommissionsReport>
  | ReturnType<typeof clearA360CommissionsReportingState>
  | ReturnType<typeof clearProducerFilter>
  | ReturnType<typeof changeCommissionsReportData>;

export default (
  state: A360CommissionReportingState = DEFAULT_STATE,
  action: A360CommissionReportingAction
): A360CommissionReportingState => {
  switch (action.type) {
    case A360CommissionReportingActionType.CHANGE_AVAILABLE_PRODUCER_CODES_FOR_REPORTING:
      return {
        ...state,
        availableProducerCodesForReporting: action.payload.producerCodes,
      };
    case A360CommissionReportingActionType.CHANGE_SELECTED_PRODUCER_CODES_FOR_REPORTING:
      return {
        ...state,
        filter: {...state.filter, selectedProducerCodes: action.payload.producerCodes},
      };
    case A360CommissionReportingActionType.IS_REPORT_DATA_LOADING:
      return {
        ...state,
        isReportDataLoading: action.payload.isReportDataLoading,
      };
    case A360CommissionReportingActionType.IS_PRODUCER_FILTER_LOADING:
      return {
        ...state,
        isProducerFilterLoading: action.payload.isProducerFilterLoading,
      };

    case A360CommissionReportingActionType.CHANGE_SELECTED_DATE_FOR_REPORTING:
      return {
        ...state,
        filter: {...state.filter, selectedDate: action.payload.selectedDate},
      };
    case A360CommissionReportingActionType.CHANGE_SELECTED_COMMISSIONS_REPORT:
      return {
        ...state,
        filter: {...state.filter, selectedReport: action.payload.selectedReport},
      };
    case A360CommissionReportingActionType.CHANGE_COMMISSIONS_REPORT_DATA:
      return {
        ...state,
        commissionsReportData: action.payload.commissionsReportData,
      };
    case A360CommissionReportingActionType.CLEAR_PRODUCER_FILTER:
      return {
        ...state,
        filter: {...state.filter, selectedProducerCodes: []},
      };
    case A360CommissionReportingActionType.CLEAR_A360_COMMISSIONS_REPORTING_STATE:
      return DEFAULT_STATE;

    default:
      return state;
  }
};
